import { useEffect, useMemo, useState } from "react";
import {
	Box,
	Button,
	Checkbox,
	CircularProgress,
	FormControl,
	InputLabel,
	ListItemText,
	MenuItem,
	OutlinedInput,
	Paper,
	Select,
	Switch,
	Typography,
} from "@mui/material";

import {
	fetchData,
	FetchDataFailureAction,
	FetchDataSuccessAction,
} from "../../../store/actions";
import { getVelocityData, jiraChartResponseProfile } from "../../../Service/Apis";
import { ThunkDispatch } from "@reduxjs/toolkit";
import { RootState } from "../../../store/Store";
import { useDispatch, useSelector } from "react-redux";
import MonthButtonGroup from "../../utilization/MonthsButtonGroup";
import { useCustomToast } from "../../Methods/SnackBar";
import LineChartComponent from "../../charts/LineChart";
import { isEmployeeAccessRole } from "../../../utils/accessRole";
import {
	jiraChartBlobDownload,
	resolveJiraExportEmployeeIds,
} from "../chartExport";

const MONTH_TO_LABEL: Record<string, string> = {
	"1": "1M",
	"3": "3M",
	"6": "6M",
	"12": "1Y",
};

const VelocityChart = () => {
	const dispatch2: ThunkDispatch<
		RootState,
		null,
		FetchDataSuccessAction | FetchDataFailureAction
	> = useDispatch();
	const [velocityData, setVelocityData] = useState<any>([]);
	const [selectedProjects, setSelectedProjects] = useState<string[]>([]);
	const [month, setMonth] = useState<string>("1");
	const [selectedFormat, setSelectedFormat] = useState<string>("excel");
	const showToast = useCustomToast();
	const [selectedEmployees, setSelectedEmployees] = useState<string[]>([]);
	const [loading, setLoading] = useState(false);
	const [downloading, setDownloading] = useState(false);
	const [selectAll, setSelectAll] = useState(true);

	const nrole = useSelector((state: any) => state.auth.selectedRole);
	const paramsData = useMemo(
		() => ({ ...jiraChartResponseProfile, role: nrole ?? "Employee" }),
		[nrole]
	);

	const fetchVelocityData = async (selectedMonth: string) => {
		try {
			setLoading(true);
			const res = await dispatch2(
				fetchData(`${getVelocityData}${selectedMonth}`, paramsData)
			);
			setMonth(selectedMonth);
			setVelocityData(res);
		} catch (error) {
			console.error(error);
		} finally {
			setLoading(false);
		}
	};

	useEffect(() => {
		fetchVelocityData("1");
	}, []);

	useEffect(() => {
		if (velocityData?.projects?.length) {
			setSelectedProjects(
				velocityData.projects.map((p: any) => p.project_name)
			);
		}
	}, [velocityData]);

	const computedData = useMemo(() => {
		if (!velocityData) return [];

		if (velocityData.projects?.length) {
			const selectedProjectsData = velocityData.projects.filter(
				(p: any) => selectedProjects.includes(p.project_name)
			);
			if (!selectedProjectsData.length) return [];

			// Build a name → employee_id map across all projects for export support
			const nameToId = new Map<string, string>(
				velocityData.projects
					.flatMap((p: any) => p.employees)
					.map((e: any) => [e.employee_name, String(e.employee_id)])
			);

			// Merge velocity entries from ALL selected projects.
			// VelocityMetrics is not project-scoped — an employee in multiple projects
			// carries the same weekly velocity value in each project's issues array.
			// Deduplicate by (employee_name, week_end) so employees shared across
			// projects are not double-counted in the chart.
			const seen = new Set<string>();
			const result: any[] = [];

			for (const project of selectedProjectsData) {
				for (const issue of project.issues) {
					for (const entry of issue.velocity) {
						const key = `${entry.assignee_name}|${issue.week_end}`;
						if (!seen.has(key)) {
							seen.add(key);
							result.push({
								employee_name: entry.assignee_name,
								employee_id: nameToId.get(entry.assignee_name),
								week: issue.week_end,
								percent: entry.story_points_per_hour,
							});
						}
					}
				}
			}
			return result;
		}

		return (
			velocityData?.data?.flatMap((issue: any) =>
				issue.velocity.map((entry: any) => ({
					employee_name: entry.assignee_name,
					employee_id: undefined,
					week: issue.week_end,
					percent: entry.story_points_per_hour,
				}))
			) || []
		);
	}, [velocityData, selectedProjects]);

	const chartEmployeeNames = useMemo((): string[] => {
		return Array.from(
			new Set(
				computedData.map((item: any) => String(item.employee_name))
			)
		);
	}, [computedData]);

	useEffect(() => {
		if (!velocityData || loading) return;
		if (velocityData.projects?.length) {
			// Derive employee names from computedData (employees with actual velocity
			// data in the selected time window) rather than project.employees (the
			// full roster). An employee on the roster may have no velocity metrics for
			// the current period; including them causes selectedEmployees to diverge
			// from chartEmployeeNames, breaking the fullTeamExport check in onDownload.
			const names: string[] = Array.from(
				new Set(computedData.map((item: any) => String(item.employee_name)))
			);
			if (!names.length) return;
			setSelectedEmployees(names);
			setSelectAll(true);
		} else if (velocityData.data?.length) {
			const names: string[] = Array.from(
				new Set(
					velocityData.data.flatMap((entry: any) =>
						entry.velocity.map((v: any) => v.assignee_name)
					)
				)
			);
			setSelectedEmployees(names);
			setSelectAll(true);
		}
	}, [velocityData, loading, selectedProjects, computedData]);

	const handleMonthChange = (selectedMonth: string) => {
		fetchVelocityData(selectedMonth);
	};

	const buttonData = [
		{ label: "1M", onClick: () => handleMonthChange("1") },
		{ label: "3M", onClick: () => handleMonthChange("3") },
		{ label: "6M", onClick: () => handleMonthChange("6") },
		{ label: "1Y", onClick: () => handleMonthChange("12") },
	];

	const onDownload = async () => {
		try {
			setDownloading(true);
		const isEmployeeRole =
			(nrole ?? "Employee").toLowerCase() === "employee";

			// Collect { employee_name, employee_id } records from all selected projects
			// so buildExportNameToIdMap has a complete name→ID lookup even for employees
			// who have no velocity entries in the current time window.
			const teamEmployees = (velocityData.projects || [])
				.filter((p: any) => selectedProjects.includes(p.project_name))
				.flatMap((p: any) => p.employees);

			const employeeIds = resolveJiraExportEmployeeIds({
				selectedEmployees,
				chartEmployeeNames,
				teamEmployees,
				rowsForIdFallback: computedData as any[],
			});

			const baseParams: Record<string, unknown> = {
				...paramsData,
				download: selectedFormat,
			};
			if (employeeIds) {
				baseParams.employee_ids = employeeIds;
			}
			if (!isEmployeeRole) {
				if (selectedProjects.length === 0) {
					throw new Error("Please select at least one project");
				}
				const selectedProjectIds = (velocityData.projects || [])
					.filter((p: any) => selectedProjects.includes(p.project_name))
					.map((p: any) => p.project_id);
				baseParams.project_ids = selectedProjectIds;
			}

			const ext =
				selectedFormat === "excel" ? "xlsx" : selectedFormat;
			const isAll =
				!isEmployeeRole &&
				selectedProjects.length === (velocityData.projects || []).length;
			const filename = isEmployeeRole
				? `velocity.${ext}`
				: isAll
				? `velocity-all-projects.${ext}`
				: `velocity-${selectedProjects
						.slice(0, 2)
						.join("-")
						.replace(/\s+/g, "_")}${
						selectedProjects.length > 2
							? `-+${selectedProjects.length - 2}more`
							: ""
				  }.${ext}`;

			await jiraChartBlobDownload(
				`${getVelocityData}${month}`,
				baseParams,
				filename,
				showToast
			);
		} catch (error: any) {
			showToast(error?.message ?? String(error), "error", true);
		} finally {
			setDownloading(false);
		}
	};

	const handleEmployeeSelection = (employeeName: string) => {
		const newSelectedEmployees = selectedEmployees.includes(
			employeeName
		)
			? selectedEmployees.filter((name) => name !== employeeName)
			: [...selectedEmployees, employeeName];

		setSelectedEmployees(newSelectedEmployees);

		const allNames = getAllEmployeeNames();
		setSelectAll(newSelectedEmployees.length === allNames.length);
	};

	const getAllEmployeeNames = () => {
		if (velocityData?.projects?.length) {
			// Return the same set the chart displays (employees with velocity data)
			// so the "Select All" toggle stays in sync with chartEmployeeNames.
			return chartEmployeeNames;
		}
		return Array.from(
			new Set<string>(
				velocityData.data?.flatMap((entry: any) =>
					entry.velocity.map((item: any) => item.assignee_name as string)
				) || []
			)
		);
	};

	const handleSwitchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		const checked = event.target.checked;
		setSelectAll(checked);

		if (checked) {
			setSelectedEmployees(getAllEmployeeNames());
		} else {
			setSelectedEmployees([]);
		}
	};

	const handleProjectChange = (event: any) => {
		const value = event.target.value;
		const allProjectNames = (velocityData?.projects || []).map(
			(p: any) => p.project_name
		);
		setSelectedProjects(
			value[value.length - 1] === "all"
				? selectedProjects.length === allProjectNames.length
					? []
					: allProjectNames
				: value
		);
	};

	return (
		<>
			<Paper sx={{ m: 2, p: 4 }}>
				<Box display="flex" justifyContent="space-between" alignItems="center">
					<Box
						display="flex"
						justifyContent="center"
						alignItems="center"
						flexDirection="column"
						gap={2}
					>
						<Typography variant="h4">Velocity Chart</Typography>
						<MonthButtonGroup
							buttonData={buttonData}
							value={MONTH_TO_LABEL[month] ?? "1M"}
						/>
					</Box>
					<Box
						display="flex"
						justifyContent="center"
						alignItems="center"
						flexDirection="column"
						gap={2}
					>
						{velocityData?.projects?.length > 0 && (
							<FormControl
								size="small"
								sx={{ minWidth: 220, width: "100%", maxWidth: 320 }}
							>
								<InputLabel id="velocity-project-select-label">
									Select Project
								</InputLabel>
								<Select
									labelId="velocity-project-select-label"
									id="velocity-project-select"
									multiple
									value={selectedProjects}
									onChange={handleProjectChange}
									input={
										<OutlinedInput label="Select Project" />
									}
									renderValue={(selected) =>
										(selected as string[]).length ===
										velocityData.projects.length
											? "All Projects"
											: `Selected Projects (${(selected as string[]).length})`
									}
								>
									<MenuItem value="all">
										<Checkbox
											checked={
												selectedProjects.length ===
												velocityData.projects.length
											}
										/>
										<ListItemText primary="Select All" />
									</MenuItem>
									{velocityData.projects.map((project: any) => (
										<MenuItem
											key={project.project_id}
											value={project.project_name}
										>
											<Checkbox
												checked={selectedProjects.includes(
													project.project_name
												)}
											/>
											<ListItemText
												primary={project.project_name}
											/>
										</MenuItem>
									))}
								</Select>
							</FormControl>
						)}
						<Box
							display="flex"
							justifyContent="center"
							gap={2}
							alignItems="center"
							flexWrap="wrap"
						>
							<FormControl size="small" sx={{ minWidth: 120 }}>
								<InputLabel id="velocity-export-format-label">
									Format
								</InputLabel>
								<Select
									labelId="velocity-export-format-label"
									label="Format"
									value={selectedFormat}
									onChange={(e) =>
										setSelectedFormat(e.target.value)
									}
								>
									<MenuItem value="excel">
										Excel (.xlsx)
									</MenuItem>
									<MenuItem value="csv">CSV</MenuItem>
									<MenuItem value="json">JSON</MenuItem>
								</Select>
							</FormControl>
							{downloading ? (
								<Box
									display="flex"
									justifyContent="center"
									alignItems="center"
									gap={2}
								>
									<CircularProgress size="1rem" />
									<Typography>Downloading...</Typography>
								</Box>
							) : (
								<Button
									variant="outlined"
									size="small"
									onClick={onDownload}
								>
									Export
								</Button>
							)}
						</Box>
					</Box>
				</Box>
				{!isEmployeeAccessRole(nrole) && (
					<Box
						display="flex"
						justifyContent="flex-end"
						alignItems="center"
						gap={2}
						m={2}
						width="100%"
					>
						<Typography>Select All Employees</Typography>
						<Switch
							checked={selectAll}
							onChange={handleSwitchChange}
							color="primary"
						/>
					</Box>
				)}
				{loading ? (
					<Box
						display="flex"
						justifyContent="center"
						alignItems="center"
						width="100%"
						height={200}
					>
						<CircularProgress />
					</Box>
				) : (
					<LineChartComponent
						data={computedData || []}
						YAxisLabel="Story Points per Hour"
						selectedEmployees={selectedEmployees}
						onEmployeeSelection={handleEmployeeSelection}
					/>
				)}
			</Paper>
		</>
	);
};

export default VelocityChart;
